import {
  IAddressResponseData,
  Checkout,
  Customer,
  ICustomerDataset,
  ICheckoutData,
  IGetCheckoutShippingOptionsData,
} from "../../../typescript-library";
import * as Sentry from "@sentry/browser";
import * as React from "react";
import {PropsWithChildren, useCallback, useRef, useState} from "react";
import {IViewState, useCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import {PaymentController} from "../../lib/payments/PaymentController";
import window from "../../lib/WindowExtended";
import {CheckoutStep} from "../Checkout";
import {useCheckoutContext} from "./CheckoutContextProvider";
import {convertToAddressData, isToNorthIreland} from "../steps/AddressStep/AddressStep";
import {sentryAddBreadcrumb} from "../../../helpers/sentryHelper";
import {DELIVERY_ADDRESS_NOT_ALLOWED, INVALID_VAT_NUMBER, NO_SHIPPING_SELECTED} from "../../../helpers/errorHandling";
import {useCustomerSelector} from "../../../store";

type DeliveryTypeType = "Packstation" | "Postfiliale" | "Privat";

interface ICheckoutViewContext extends ICheckoutViewContextState {
  gotoStep: (step?: CheckoutStep) => void;
}

interface ICheckoutViewContextState {
  deliveryCountry?: string;
  step: CheckoutStep;
  shippingOptions: IGetCheckoutShippingOptionsData[];
}

interface ICheckoutViewContextStateInternal extends ICheckoutViewContextState {
  deliveryType: DeliveryTypeType;
  zip: string;
}

const checkoutViewContext = React.createContext<ICheckoutViewContext | null>(null);

export const useCheckoutViewContext = (): ICheckoutViewContext => {
  const context = React.useContext(checkoutViewContext);
  if (!context) {
    throw new Error("useCheckoutViewContext must be used in a component within a CheckoutViewContextProvider.");
  }

  return context;
};

const toStopAtLogin = (customer: Customer, viewState: IViewState): boolean => {
  return !(
    customer.isLoggedIn ||
    viewState.isRegistering ||
    viewState.paypalInfos ||
    viewState.gPayData ||
    viewState.appleData
  );
};

const hasValidAddresses = (checkout: Checkout): boolean => {
  if (isToNorthIreland(checkout.deliveryAddress?.countryCode ?? "", checkout.deliveryAddress?.zip ?? "")) {
    return false;
  }
  // if addresses valid, return true to skip address step
  if (isAddressesValid(checkout)) {
    return true;
  }

  // by default return false, or true if the user
  // is into changing phone number in shipping step

  return window.isIntoSettingPhoneNoInShippingStep;
};

const toStopAtAddressStep = (checkout: Checkout) => {
  return (
    !hasValidAddresses(checkout) ||
    !!checkout.cartData.positions.find((pos) => {
      return !!pos.messages.find((msg) => msg.code === "NOT_ALLOWED_FOR_COUNTRY" || msg.code === "SHIPPING_NOT_FOUND");
    }) ||
    checkout.cartData.messages.some((msg) => msg.code === INVALID_VAT_NUMBER) ||
    window.isIntoChangingCountry
  );
};

const toStopAtShippingStep = (checkout: Checkout, shippingOptions: IGetCheckoutShippingOptionsData[] | undefined) => {
  return (
    !checkout.logisticsTypeId ||
    !shippingOptions?.find((ship) => ship.logisticsTypeId === checkout.logisticsTypeId) ||
    checkout.cartData.messages.find((msg) => msg.code === NO_SHIPPING_SELECTED) ||
    checkout.messages.some((msg) => msg.code === DELIVERY_ADDRESS_NOT_ALLOWED) ||
    (shippingOptions?.find((ship) => ship.logisticsTypeId === checkout.logisticsTypeId)?.isPhoneRequired &&
      (!checkout.deliveryAddress?.phone || !!checkout.deliveryAddress?.messages?.find((msg) => msg.field === "phone")))
  );
};

/**
 * return appropriate step if none is given
 * @param checkout
 * @param customer
 * @param viewState
 * @param shippingOptions
 */
const returnAppropriateStep = (
  checkout: Checkout,
  customer: Customer,
  viewState: IViewState,
  shippingOptions: IGetCheckoutShippingOptionsData[],
): CheckoutStep => {
  const isItemPositionsInCart = !!checkout.cartData.positions.find(
    (position) => position.reference && position.reference.itemId !== null && !!position.item,
  );

  let returnStep: CheckoutStep;

  if (!isItemPositionsInCart) {
    returnStep = CheckoutStep.CARTERRORS;
  } else if (toStopAtLogin(customer, viewState)) {
    returnStep = CheckoutStep.LOGIN;
  } else if (toStopAtAddressStep(checkout)) {
    returnStep = CheckoutStep.ADDRESS;
  } else if (toStopAtShippingStep(checkout, shippingOptions)) {
    returnStep = CheckoutStep.SHIPPING;
  } else if (!checkout.paymentId || !PaymentController.isDataComplete()) {
    returnStep = CheckoutStep.PAYMENT;
  } else if (!checkout.isCompleted) {
    returnStep = CheckoutStep.SUMMARY;
  } else {
    returnStep = CheckoutStep.FINISHED;
  }

  return returnStep;
};

/**
 * Context provider for view params
 * @param props
 * @constructor
 */
const CheckoutViewContextProvider: React.FC<PropsWithChildren> = ({children}) => {
  const {customer} = useCustomerSelector();
  const {setViewState, viewState} = useCartContext();
  const {checkout} = useCheckoutContext();
  const viewStateRef = useRef<IViewState>(viewState);
  const [checkoutViewContextState, setCheckoutViewContextState] = useState<ICheckoutViewContextStateInternal | null>(
    null,
  );
  const checkoutViewRef = useRef<ICheckoutViewContextStateInternal | null>();

  checkoutViewRef.current = checkoutViewContextState;
  viewStateRef.current = viewState;

  const gotoStep = useCallback(
    (nextStep?: CheckoutStep) => {
      const tempNextStep =
        nextStep ||
        returnAppropriateStep(checkout, customer, viewState, checkoutViewRef.current?.shippingOptions ?? []);
      sentryAddBreadcrumb(
        tempNextStep,
        checkout,
        "Breadcrumb or Summary",
        checkoutViewRef.current?.shippingOptions,
        customer,
      );
      setCheckoutViewContextState((prevState) => {
        if (!prevState) {
          return prevState;
        }

        return {
          ...prevState,
          step: tempNextStep,
        };
      });
    },
    [checkout.checkoutId],
  );

  const updateState = useCallback(
    (shippingOptions: IGetCheckoutShippingOptionsData[], reason: string) => {
      const appropriateStep = returnAppropriateStep(checkout, customer, viewStateRef.current, shippingOptions);

      const deliveryType = getDeliveryType(checkout.deliveryAddress);

      sentryAddBreadcrumb(appropriateStep, checkout, reason, shippingOptions, customer);
      setCheckoutViewContextState({
        deliveryCountry: checkout.cartData.countryCode,
        shippingOptions,
        deliveryType,
        zip: checkout.deliveryAddress?.zip ?? "",
        step: appropriateStep,
      });
    },
    [checkout.checkoutId],
  );

  const isExternalDeliveryAddressSetAndPossible = useCallback(
    (shippingOptions: IGetCheckoutShippingOptionsData[]) => {
      const deliveryType = getDeliveryType(checkout.deliveryAddress);
      if (deliveryType !== "Privat") {
        const shippingToPackingStation = deliveryType === "Packstation";
        const tempShippingOptions = shippingOptions.filter((option) =>
          shippingToPackingStation ? option.isPackingStationAllowed : option.isPostOfficeAllowed,
        );

        if (tempShippingOptions.length) {
          // 'Packstation' / 'Postfiliale', but still some shippings capable of that
          return [];
        } else {
          // No shipping available. Return delivery type specific error
          return shippingToPackingStation
            ? ["delivery to packing station not available for logistics type."]
            : ["delivery to post office not available for logistics type."];
        }
      } else {
        // No delivery address or a private street
        return [];
      }
    },
    [checkout.checkoutId],
  );

  const initShippings = useCallback(async () => {
    let shippingOptions: IGetCheckoutShippingOptionsData[];
    try {
      shippingOptions = await checkout.getAvailableShippingOptions();
      const shippingError = isExternalDeliveryAddressSetAndPossible(shippingOptions);
      if (shippingError.length) {
        setViewState({
          ...viewState,
          shippingErrors: shippingError,
        });
        await checkout.put({
          address: {
            invoice: checkout.invoiceAddress ? convertToAddressData(checkout.invoiceAddress) : undefined,
            delivery: convertToAddressData({
              ...checkout.deliveryAddress,
              street: "",
              number: "",
              zip: "",
              city: "",
            }),
          },
        });
      } else if (viewStateRef.current.shippingErrors.length) {
        setViewState({...viewStateRef.current, shippingErrors: []});
      }
    } catch (err) {
      shippingOptions = [];
      Sentry.captureException(err, {
        extra: {occurence: "CheckoutViewContext.initAllThings()", countryCode: checkout.cartData.countryCode},
      });
    }

    updateState(shippingOptions, "checkout view init");
  }, [checkout.checkoutId]);

  const updateShippings = useCallback(
    async (data: ICheckoutData | ICustomerDataset) => {
      const reason = typeof (data as ICheckoutData).checkoutId !== "undefined" ? "checkout emit" : "customer emit";
      const cartCountry = checkout.cartData.countryCode;
      let shippingOptions: IGetCheckoutShippingOptionsData[] = checkoutViewRef.current?.shippingOptions ?? [];

      const isShippingInvalid = checkout.messages.some((msg) => msg.code === DELIVERY_ADDRESS_NOT_ALLOWED);
      const deliveryTypeChanged = checkoutViewRef.current?.deliveryType !== getDeliveryType(checkout.deliveryAddress);
      const zipChanged = checkout.deliveryAddress?.zip !== checkoutViewRef.current?.zip;

      if (
        cartCountry !== checkoutViewRef.current?.deliveryCountry ||
        !shippingOptions.length ||
        isShippingInvalid ||
        deliveryTypeChanged ||
        zipChanged
      ) {
        try {
          shippingOptions = await checkout.getAvailableShippingOptions();
        } catch (err) {
          shippingOptions = [];
          Sentry.captureException(err, {
            extra: {occurence: "CheckoutViewContext.setAllThings()", countryCode: checkout.cartData.countryCode},
          });
        }
      } else if (viewStateRef.current.shippingErrors.length) {
        setViewState({...viewStateRef.current, shippingErrors: []});
      }

      updateState(shippingOptions, reason);
    },
    [checkout.checkoutId],
  );

  React.useEffect(() => {
    void initShippings();

    checkout.emitter.on(updateShippings);

    /*
      Next line _must_ be set due to _no_ change in cart/checkout, thus,
      no emit, when customer changes from "known" to "logged in".
      Even though above one builds an emitter chain with `CartContextProvider.tsx:417`
      and `CheckoutFactory.ts:77`, leading to an overhead of requests.
    */
    customer.emitter.on(updateShippings);

    return () => {
      checkout.emitter.off(updateShippings);
      customer.emitter.off(updateShippings);
    };
  }, [checkout.checkoutId]);

  if (!checkoutViewContextState) {
    return null;
  }

  const {Provider} = checkoutViewContext;

  return (
    <Provider
      value={{
        ...checkoutViewContextState,
        gotoStep,
      }}
    >
      {children}
    </Provider>
  );
};

/**
 * Check addresses for errors
 * @param checkout
 */
const isAddressesValid = (checkout: Checkout): boolean => {
  const {deliveryAddress, invoiceAddress} = checkout;

  return !(
    deliveryAddress === null ||
    invoiceAddress === null ||
    deliveryAddress.messages?.find((message) => message.type === "error") ||
    invoiceAddress.messages?.find((message) => message.type === "error")
  );
};

const getDeliveryType = (deliveryAddress: IAddressResponseData | null): DeliveryTypeType => {
  if (!deliveryAddress) {
    return "Privat";
  }

  return (
    ["Packstation", "Postfiliale"].includes(deliveryAddress.street) ? deliveryAddress.street : "Privat"
  ) as DeliveryTypeType;
};

export default CheckoutViewContextProvider;
